import { useEffect, useMemo, useState } from 'react';
import {
  GROUP_FORMATS,
  GROUP_FORMAT_LABEL,
  GROUP_STATUSES,
  GROUP_STATUS_LABEL,
  lessonsPerStage,
  stageLabel,
} from '@doit/shared';
import { Modal } from '../../components/Modal';
import { Button, Field } from '../../components/ui';
import { Select, WeekdayPicker } from '../../components/form';
import { useToast } from '../../components/Toast';
import { useFormErrors } from '../../lib/useFormErrors';
import {
  useCourses,
  useDirections,
  useFlows,
  useProjects,
  useSaveGroup,
} from '../../api/queries';
import type { GroupDetail } from '../../api/types';

const isoDay = (v: string | null | undefined) => (v ? v.slice(0, 10) : '');

export function GroupModal({
  open,
  onClose,
  group,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  group: GroupDetail | null;
  onCreated?: (id: string) => void;
}) {
  const toast = useToast();
  const save = useSaveGroup();
  const { fieldErrors, reset, handle } = useFormErrors();

  const { data: projects } = useProjects();
  const { data: directions } = useDirections();

  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [flowId, setFlowId] = useState('');
  const [directionId, setDirectionId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [currentStage, setCurrentStage] = useState('1');
  const [format, setFormat] = useState<string>('ONLINE');
  const [status, setStatus] = useState<string>('PLANNED');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [startTime, setStartTime] = useState('20:00');
  const [durationMin, setDurationMin] = useState('120');
  const [totalLessons, setTotalLessons] = useState('0');
  const [room, setRoom] = useState('');


  // ნაკადებიცა და საგნებიც პროექტს მიჰყვება — არასწორი კომბინაცია ვერ აირჩევა
  const { data: flows } = useFlows(projectId || undefined);
  const { data: courses } = useCourses({ projectId: projectId || undefined });

  useEffect(() => {
    if (!open) return;
    reset();
    setName(group?.name ?? '');
    setProjectId(group?.flow.project.id ?? '');
    setFlowId(group?.flow.id ?? '');
    setDirectionId(group?.course.direction.id ?? '');
    setCourseId(group?.course.id ?? '');
    setCurrentStage(String(group?.currentStage ?? 1));
    setFormat(group?.format ?? 'ONLINE');
    setStatus(group?.status ?? 'PLANNED');
    setStartDate(isoDay(group?.startDate));
    setEndDate(isoDay(group?.endDate));
    setWeekdays(group?.weekdays ?? []);
    setStartTime(group?.startTime ?? '20:00');
    setDurationMin(String(group?.durationMin ?? 120));
    setTotalLessons(String(group?.totalLessons ?? 0));
    setRoom(group?.room ?? '');
  }, [open, group, reset]);

  // მიმართულების ფილტრი კურსების სიაზე
  const visibleCourses = useMemo(
    () => (courses ?? []).filter((c) => !directionId || c.direction.id === directionId),
    [courses, directionId],
  );

  // მიმართულების ან პროექტის შეცვლისას არჩეული საგანი შეიძლება აღარ ერგებოდეს.
  // `courses` ჩატვირთვამდე სია ცარიელია — მაშინ გასუფთავება არჩევანს დაკარგავდა.
  useEffect(() => {
    if (!courses) return;
    if (courseId && !visibleCourses.some((c) => c.id === courseId)) setCourseId('');
  }, [courses, visibleCourses, courseId]);

  // ახალ ჯგუფზე ლექციები და ხანგრძლივობა საგნიდან იგება
  const selectedCourse = visibleCourses.find((c) => c.id === courseId);
  useEffect(() => {
    if (group || !selectedCourse) return;
    if (totalLessons === '0' && selectedCourse.hoursPerStage > 0) {
      setTotalLessons(
        String(lessonsPerStage(selectedCourse.hoursPerStage, selectedCourse.lessonHours)),
      );
    }
    setDurationMin(String(selectedCourse.lessonHours * 60));
  }, [selectedCourse, group, totalLessons]);

  // პროექტის შეცვლისას ძველი ნაკადი აღარ ერგება (საგანს ზემოთა ეფექტი უვლის)
  useEffect(() => {
    if (!flows) return;
    if (flowId && !flows.some((f) => f.id === flowId)) setFlowId('');
  }, [flows, flowId]);

  async function submit() {
    reset();
    try {
      const saved = await save.mutateAsync({
        id: group?.id,
        data: {
          name,
          flowId,
          courseId,
          currentStage,
          format,
          status,
          startDate,
          endDate,
          weekdays,
          startTime,
          durationMin,
          totalLessons,
          room,
        },
      });
      toast.success(group ? 'ჯგუფი განახლდა' : 'ჯგუფი შეიქმნა');
      onClose();
      if (!group) onCreated?.(saved.id);
    } catch (err) {
      handle(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={group ? `ჯგუფი ${group.name}` : 'ახალი ჯგუფი'}
      description="განრიგი გამოიყენება გაკვეთილების ავტომატური გენერაციისთვის"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            გაუქმება
          </Button>
          <Button size="sm" onClick={() => void submit()} loading={save.isPending}>
            შენახვა
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* --- იდენტიფიკაცია --- */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="ჯგუფის სახელი"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
            placeholder="FEMW5-20-GV"
            hint="კურსი + ნაკადი + საათი + ლექტორის ინიციალები"
            autoFocus
          />
          <Select
            label="პროექტი"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            placeholder="აირჩიე პროექტი"
            options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
            hint="ნაკადებსა და საგნებს ფილტრავს"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="ნაკადი"
            value={flowId}
            onChange={(e) => setFlowId(e.target.value)}
            error={fieldErrors.flowId}
            placeholder="აირჩიე ნაკადი"
            options={(flows ?? []).map((f) => ({ value: f.id, label: f.name }))}
          />
          <Select
            label="მიმართულება"
            value={directionId}
            onChange={(e) => setDirectionId(e.target.value)}
            placeholder="ყველა მიმართულება"
            options={(directions ?? []).map((d) => ({ value: d.id, label: d.name }))}
            hint="მხოლოდ საგნების გასაფილტრად"
          />
          <Select
            label="საგანი"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            error={fieldErrors.courseId}
            placeholder="აირჩიე საგანი"
            options={visibleCourses.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>

        {/* --- ატრიბუტები --- */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="მიმდინარე ეტაპი"
            value={currentStage}
            onChange={(e) => setCurrentStage(e.target.value)}
            error={fieldErrors.currentStage}
            options={Array.from(
              {
                length: Math.max(
                  selectedCourse?.stageCount ?? 1,
                  group?.course.stageCount ?? 1,
                  Number(currentStage) || 1,
                ),
              },
              (_, i) => ({
                value: String(i + 1),
                label: stageLabel(i + 1),
              }),
            )}
            hint="გაკვეთილები და დასწრება ამ ეტაპს მიება"
          />
          <Select
            label="ფორმატი"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            options={GROUP_FORMATS.map((f) => ({ value: f, label: GROUP_FORMAT_LABEL[f] }))}
          />
          <Select
            label="სტატუსი"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={GROUP_STATUSES.map((s) => ({ value: s, label: GROUP_STATUS_LABEL[s] }))}
          />
        </div>

        {/* --- განრიგი --- */}
        <div className="rounded-lg border border-line bg-canvas/50 p-4">
          <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-ink-mute">
            განრიგი
          </p>

          <div className="space-y-4">
            <WeekdayPicker
              value={weekdays}
              onChange={setWeekdays}
              error={fieldErrors.weekdays}
              hint="მაგ. ორშ + ხუთ — გაკვეთილები კვირაში ორჯერ"
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="დაწყების თარიღი"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                error={fieldErrors.startDate}
              />
              <Field
                label="დასრულების თარიღი"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                error={fieldErrors.endDate}
                hint="არასავალდებულო"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label="დაწყების დრო"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                error={fieldErrors.startTime}
              />
              <Field
                label="ხანგრძლივობა (წთ)"
                type="number"
                min={15}
                step={15}
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                error={fieldErrors.durationMin}
              />
              <Field
                label="ლექციების რაოდენობა"
                type="number"
                min={0}
                value={totalLessons}
                onChange={(e) => setTotalLessons(e.target.value)}
                error={fieldErrors.totalLessons}
              />
            </div>
          </div>
        </div>

        {/* --- დამატებითი --- */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="აუდიტორია"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            error={fieldErrors.room}
            placeholder="ონლაინ / 204"
          />
        </div>
      </div>
    </Modal>
  );
}
